import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import {
  Appointment,
  AppointmentFilters,
  ActiveTab,
  ViewMode,
  DateRange,
} from "../types/appointment.types";

interface AppointmentContextType {
  appointments: Appointment[];
  filters: AppointmentFilters;
  activeTab: ActiveTab;
  viewMode: ViewMode;
  isLoading: boolean;
  setAppointments: (appointments: Appointment[]) => void;
  setFilters: (filters: Partial<AppointmentFilters>) => void;
  setActiveTab: (tab: ActiveTab) => void;
  setViewMode: (mode: ViewMode) => void;
  setDateRange: (dateRange: DateRange) => void;
  setIsLoading: (loading: boolean) => void;
}

const AppointmentContext = createContext<AppointmentContextType | undefined>(
  undefined
);

export const useAppointmentContext = () => {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error(
      "useAppointmentContext must be used within AppointmentProvider"
    );
  }
  return context;
};

interface AppointmentProviderProps {
  children: ReactNode;
}

export const AppointmentProvider: React.FC<AppointmentProviderProps> = ({
  children,
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>("upcoming");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filters, setFiltersState] = useState<AppointmentFilters>({
    provider: "all",
    appointmentMode: "all",
    dateRange: {
      start: null,
      end: null,
    },
  });

  const setFilters = useCallback((newFilters: Partial<AppointmentFilters>) => {
    setFiltersState((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const setDateRange = useCallback((dateRange: DateRange) => {
    setFiltersState((prev) => ({
      ...prev,
      dateRange,
    }));
  }, []);

  return (
    <AppointmentContext.Provider
      value={{
        appointments,
        filters,
        activeTab,
        viewMode,
        isLoading,
        setAppointments,
        setFilters,
        setActiveTab,
        setViewMode,
        setDateRange,
        setIsLoading,
      }}
    >
      {children}
    </AppointmentContext.Provider>
  );
};
